import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  Fragment,
} from "react";
import { Box } from "@mui/material";
import { ParagraphText, UBButton, UBInput } from "./../../../Libraries";
import { LanguageContext } from "../../../context/LanguageContext";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { ReactComponent as CorrectIcon } from "./../../../assets/icons/correct-icon.svg";
import { removeLeadingZeros } from "./../../../utils";
import { PlusBlueIcon, PlusgreyIcon } from "../../../assets/icons/icon";
import styles from "./styles.module.css";
import { sentenceCase } from "../../../constants";

import { analytics, triggerLogEvent } from "../../../config/firebase";

// Inline style beats CSS, so the dark value comes in via --openAmountText (set in styles.module.css)
const AMOUNT_FILLED_COLOR = "var(--openAmountText, #1d2939)";

const NumericKeypad = ({
  onSubmit,
  onChangeNoteText,
  noteText,
  handleNoteLogEvent = () => {},
  handleOpenItemLogEvent = () => {},
}) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const debounceNote = useDebouncedValue(note, 500);
  const [I18n] = useContext(LanguageContext);
  const inputRef = useRef(null);

  const handleChange = (key, value) => {
    if (value.length > 10) {
      setErrorMessage(I18n.max_limit_reached);
      return;
    }
    if (value.length >= 1 && amount.length == 0) handleOpenItemLogEvent();
    setAmount(value);
    setErrorMessage("");
  };

  const concateNumber = (number) => {
    const inputElement = inputRef.current;
    const start = inputElement.selectionStart;
    const end = inputElement.selectionEnd;

    const newAmount = amount.slice(0, start) + number + amount.slice(end);
    if (newAmount.length <= 10) {
      if (newAmount.length >= 1 && amount.length == 0) handleOpenItemLogEvent();
      setAmount(newAmount);
      setErrorMessage("");
      setTimeout(() => {
        inputElement.setSelectionRange(start + 1, start + 1);
      }, 0);
    } else {
      setErrorMessage(I18n.max_limit_reached);
    }
  };

  const handleDelete = () => {
    const inputElement = inputRef.current;
    const start = inputElement.selectionStart;
    const end = inputElement.selectionEnd;
    inputElement?.focus();
    if (start === 0 && end === 0) return;

    const newAmount = amount.slice(0, start - 1) + amount.slice(end);
    setAmount(newAmount);
    setErrorMessage("");

    setTimeout(() => {
      inputElement.setSelectionRange(start - 1, start - 1);
    }, 0);
  };

  const onSubmitForm = (e) => {
    e.preventDefault();
    if (!(amount > 0)) return;
    const value = removeLeadingZeros(amount);
    onSubmit(value);
    triggerLogEvent(analytics, "pos_item_added", { opening_amount: value });
    setAmount("");
    setErrorMessage("");
  };

  useEffect(() => {
    if (onChangeNoteText) {
      onChangeNoteText(debounceNote);
    }
  }, [debounceNote, onChangeNoteText]);

  useEffect(() => {
    setNote(noteText);
  }, [noteText]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const activeElement = document.activeElement;
      const tagName = activeElement.tagName.toLowerCase();
      const isContentEditable = activeElement.isContentEditable;
      if (!event.shiftKey && event.key === "Escape") {
        inputRef.current.blur();
      }
      if (tagName === "input" || tagName === "textarea" || isContentEditable) {
        return;
      }
      if (!event.shiftKey && event.key === "Enter") {
        inputRef.current.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    // Cleanup on unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Box
      className={`${styles.keypadContainer} numeric-keypad-container`}
    >
      <Box className={styles.keypadHeader}>
        <ParagraphText className={styles.enterAmountText}>
          {I18n.enter_open_amount}
        </ParagraphText>

        <div className={styles.rupeesInputBox}>
          <div className={styles.keypadRowWrapper}>
            <form
              className={styles.keypadFormWrapper}
              onSubmit={onSubmitForm}
            >
              <ParagraphText
                className={styles.rsText}
                style={{ color: amount ? AMOUNT_FILLED_COLOR : "#94a3b8" }}
              >
                Rs.
              </ParagraphText>
              <UBInput
                inputId="open_amount_id"
                inputRef={inputRef}
                placeholder=""
                className={styles.saleBookRupees}
                baseClassname={styles.saleBookRupeesWrapper}
                style={{ color: amount ? AMOUNT_FILLED_COLOR : "#94a3b8" }}
                inputClassname="sale-rupees-input"
                value={amount}
                onChange={handleChange}
                onlyNumbers={true}
                autoFocus={true}
                maxLength={11}
              />
            </form>
          </div>

          <span className="fontSize12 error marginBottom15">
            {errorMessage}
          </span>
        </div>
      </Box>

      {/* Note Input */}
      <UBInput
        inputId="note_id"
        placeholder={I18n.note_placeholder}
        value={note}
        baseClassname={styles.saleBookNote}
        onChange={(key, value) => {
          if (value.length >= 1 && note.length == 0) handleNoteLogEvent();
          setNote(value);
        }}
        maxLength={100}
      />

      <div className={`${styles.numpadWrapper} numpadWrapper`}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, <CorrectIcon />, 0].map((value, index) => (
          <UBButton
            key={index}
            id={
              typeof value === "object"
                ? "keypad_backspace_id"
                : `keypad_${value}_id`
            }
            className={`${styles.numbericBtnStyle} numbericBtnStyle`}
            onClick={() =>
              typeof value === "object"
                ? handleDelete()
                : concateNumber(value)
            }
            title={typeof value === "object" ? <CorrectIcon /> : value}
          />
        ))}
        <UBButton
          id={`add_item_id`}
          className={`${styles.submitBtnStyle}${
            +amount > 0 ? ` ${styles.submitBtnStyleActive}` : ""
          }`}
          disabled={!(+amount > 0)}
          onClick={onSubmitForm}
          title={
            <Fragment>
              
                {+amount > 0 ? <PlusBlueIcon /> : <PlusgreyIcon />}              
                {`${sentenceCase(I18n.add_to_sale)} (${I18n.enter})`}
              
            </Fragment>
          }
        />
      </div>
    </Box>
  );
};

export default NumericKeypad;
